import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateUTCToLocale } from 'src/common/helpers/utcToLocale';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { setSummaryUpload } from 'src/redux/slices/therapy.slice';
import { downloadSummaryResponse } from 'src/redux/state.types';
import { getIntakeFormUsecases, getSummaryReportUsecase } from 'src/services/therapy/useCases';
import PopUp from 'src/ui/Popup';
import SuccessNotification from 'src/views/therapyPortal/SuccessNotification';
import UploadSummaryDialog from 'src/views/therapyPortal/UploadSummaryDialog';

interface SessionProps {
  session: {
    appointmentId?: string;
    appointmentDate?: string;
    therapistName?: string;
    patientName?: string;
    status?: string;
    duration?: number;
    type?: string;
    notes?: string;
    virtualMeetingUrl?: string;
    startDate?: string;
    fullName?: string;
    appointmentStatus?: string;
    [key: string]: any;
    therapistComment: string | null;
    sessionSummaryUrl: string | null;
  };
  isTherapist?: boolean;
  isPastSession?: boolean;
}

const Session = ({ session, isPastSession }: SessionProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['therapyAppointment']);
  const [timeRemaining, setTimeRemaining] = useState<string>(session?.timeRemaining || '');
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [downloadIntakeForm, setDownloadIntakeForm] = useState<boolean>(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [showComment, setShowComment] = useState<boolean>(false);

  const { intakeFormData } = useAppSelector((state) => state.therapy);

  const appointmentDate = session?.startDate || session?.appointmentDate;
  const formattedDate = moment(appointmentDate).isValid()
    ? moment(appointmentDate).format('MMMM DD, YYYY')
    : t('DateUnavailable');
  const formattedTime = moment(appointmentDate).isValid()
    ? moment(appointmentDate).format('hh:mm A')
    : t('TimeUnavailable');
  const durationInMinutes = session?.duration || 60;
  const timeZone = 'Eastern Standard Time';
  const displayName = session?.fullName || session?.therapistName || t('Provider');
  const sessionType = session?.type || t('VideoSession');
  // const isPastSession = moment(appointmentDate).isBefore(moment());

  const { summaryUpload, summaryDownloadData } = useAppSelector((state) => state.therapy);

  // Calculate and update the time remaining for upcoming sessions
  useEffect(() => {
    if (isPastSession || !appointmentDate) return;

    const calculateTimeRemaining = () => {
      const now = moment();
      const localTime = dateUTCToLocale(appointmentDate);
      const sessionTime = moment(localTime);

      if (sessionTime.isBefore(now)) return '00:00:00';

      const duration = moment.duration(sessionTime.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    };

    setTimeRemaining(calculateTimeRemaining());
    const timer = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, [appointmentDate, isPastSession]);

  // Handle downloading the intake form
  useEffect(() => {
    if (intakeFormData && downloadIntakeForm) {
      try {
        const byteCharacters = atob(intakeFormData.base64Data);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `IntakeResponses_${session.appointmentId}.pdf`;
        document.body.appendChild(link);
        link.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(link);
        setDownloadIntakeForm(false);
      } catch (error) {
        console.error('Error decoding Base64 data or downloading the file:', error);
      }
    }
  }, [downloadIntakeForm, intakeFormData, session.appointmentId]);

  useEffect(() => {
    if (summaryDownloadData) {
      handleDownloadFile(summaryDownloadData);
    }
  }, [summaryDownloadData]);

  const toggleDetails = () => setIsDetailsOpen(!isDetailsOpen);

  const handleViewIntakeResponses = () => {
    dispatch(getIntakeFormUsecases(session.appointmentId));
    setDownloadIntakeForm(true);
  };

  const handleUploadSessionSummary = () => {
    setIsUploadDialogOpen(true);
  };

  const handleJoinMeeting = () => {
    if (session?.meetingUrl) {
      window.open(session.meetingUrl, '_blank');
    }
  };

  const parseTimeRemaining = (time: string): number => {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 60 + minutes + seconds / 60;
  };

  const handleDownloadSessionSummary = () => {
    dispatch(getSummaryReportUsecase(session.appointmentId));
  };

  const handleDownloadFile = (data: downloadSummaryResponse) => {
    if (!data.downloadUrl) {
      return;
    }

    try {
      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = 'SessionSummary.pdf'; // You can customize the file name
      document.body.appendChild(link);
      link.click();

      // Clean up the anchor element
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };

  return (
    <>
      <div className='w-full relative'>
        <div className='px-5 pt-5 pb-3'>
          <div className='flex justify-between items-start mb-3'>
            <h3 className='font-semibold text-sm'>
              {t('SessionWith')} {displayName}
            </h3>
            <div className='flex items-center'>
              <button onClick={toggleDetails} className='flex items-center text-primary text-sm'>
                {t('ViewDetails')}
                {isPastSession && (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className={`h-4 w-4 ml-1 transition-transform ${
                      isDetailsOpen ? 'rotate-180' : ''
                    }`}
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                      clipRule='evenodd'
                    />
                  </svg>
                )}
              </button>
              {isPastSession && (
                <span
                  className={`ml-2 ${
                    session.therapistComment === null && session.sessionSummaryUrl === null
                      ? 'text-red-500'
                      : 'text-transparent'
                  }`}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                </span>
              )}
            </div>
          </div>

          <div className='flex items-center justify-between'>
            {isPastSession ? (
              <span className='text-sm'>{formattedDate}</span>
            ) : (
              <span className='text-sm text-[#042C4A]'>
                {timeRemaining} {t('hoursRemaining')}
              </span>
            )}

            {!isPastSession && (
              <button
                onClick={handleJoinMeeting}
                className={`text-grey-400 text-sm rounded-md ${
                  timeRemaining && parseTimeRemaining(timeRemaining) < 15
                    ? 'text-blue-900'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!timeRemaining || parseTimeRemaining(timeRemaining) > 15}
              >
                {t('JoinNow')}
              </button>
            )}
          </div>
        </div>

        {isDetailsOpen && (
          <div className='mt-1 bg-background p-4 rounded-md border-t border-background shadow-md hover:shadow-lg'>
            <div className='mb-4'>
              <h5 className='text-[#591D94] font-bold text-left text-lg'>{t('Details')}</h5>
              <p className='text-base mb-1 pr-20'>
                {t('A')}{' '}
                <b>
                  {durationInMinutes} {t('minute')}
                </b>{' '}
                {sessionType} {t('with')} {displayName} {t('at')} <b>{formattedTime}</b> {timeZone}{' '}
                {t('on')} <b>{formattedDate}</b>
              </p>
            </div>

            <div className='mt-4 flex flex-col space-y-3'>
              <button
                onClick={handleViewIntakeResponses}
                className='text-[#591D94] font-medium text-left text-sm pb-2'
              >
                {t('ViewIntakeResponses')}
              </button>

              {isPastSession && (
                <div className='flex justify-between items-center pt-3'>
                  <span className='text-gray-500 text-sm'>{t('TranscriptUnavailable')}</span>
                  <div>
                    {session.therapistComment === null && session.sessionSummaryUrl === null && (
                      <button
                        onClick={() => handleUploadSessionSummary()}
                        className='text-red-500 font-medium text-sm'
                      >
                        {t('AfterSessionSummary')}
                      </button>
                    )}
                    {session.therapistComment && (
                      <button
                        onClick={() => setShowComment(true)}
                        className='text-[#591D94] font-medium text-sm'
                      >
                        {t('ViewComment')}
                      </button>
                    )}
                    {session.sessionSummaryUrl && (
                      <button
                        onClick={() => handleDownloadSessionSummary()}
                        className='text-[#591D94] font-medium text-sm'
                      >
                        {t('ViewSummary')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!isPastSession && (
                <button
                  onClick={handleJoinMeeting}
                  className={`font-bold text-white text-md bg-[#591D94] border rounded-2xl w-1/2 py-1 text-center mx-auto ${
                    timeRemaining && parseTimeRemaining(timeRemaining) <= 5
                      ? ''
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!timeRemaining || parseTimeRemaining(timeRemaining) > 5}
                >
                  {t('Join')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <UploadSummaryDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        sessionId={session?.appointmentId}
      />

      <SuccessNotification
        isVisible={summaryUpload}
        onClose={() => {
          console.log('Closing Upload Dialog');
          setIsUploadDialogOpen(false); // Ensure this updates the state
          setTimeout(() => dispatch(setSummaryUpload(false)), 0); // Dispatch after state update
        }}
      />

      <PopUp isVisible={showComment} onClose={() => setShowComment(false)}>
        <div className='flex flex-col items-center justify-center w-full'>
          <span>{session.therapistComment}</span>
        </div>
      </PopUp>
    </>
  );
};

export default Session;
